import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import AnnouncementBody from './AnnouncementBody';

/* The transition dashboard a TRANSITIONING firm sees (a firm working towards
 * becoming a B2B entrepreneur firm). Purely presentational: it renders the
 * payload of GET /b2b/transition (or the staff copy from
 * GET /entrepreneur/transitions/{id}), so the firm's page and the staff preview
 * in Entrepreneur -> Transitions can never drift apart.
 *
 * Everything that depends on "today" is worked out here, in the viewer's own
 * timezone: days to Transition Day, which stage is done, per-area progress,
 * what is next and whether anything is overdue. The server only stores what
 * staff typed.
 *
 * Styles are scoped under .tdash and follow the approved mockup
 * ("Gratton & Kerr Transition Dashboard (sample)"). */

const STATUS = {
  on_track:  { label: 'On track',        bg: '#BFE0F2' },
  attention: { label: 'Needs attention', bg: '#D09B2C' },
  at_risk:   { label: 'At risk',         bg: '#F7931D' },
};
const TASK_STATUS = {
  not_started: { label: 'Not started', cls: 'n' },
  in_progress: { label: 'In progress', cls: 'a' },
  done:        { label: 'Done',        cls: 'g' },
};
const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_MS = 86400000;

// 'YYYY-MM-DD' -> a local Date at midnight. new Date('2027-01-21') would be
// UTC midnight, which is the day before anywhere west of Greenwich.
export function parseDay(s) {
  if (!s) return null;
  const [y, m, d] = String(s).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  // setFullYear, not new Date(y, ...): the constructor maps years 0-99 to
  // 1900-1999, so a mistyped "0027" would silently become 1927.
  const out = new Date(2000, m - 1, d);
  out.setFullYear(y);
  return out;
}

function startOfToday() {
  const n = new Date();
  return new Date(n.getFullYear(), n.getMonth(), n.getDate());
}

const daysBetween = (a, b) => Math.round((b - a) / DAY_MS);

// "10/15", or "1/21/27" when the date isn't in the current year.
export function shortDate(d, today = startOfToday()) {
  if (!d) return '';
  const base = `${d.getMonth() + 1}/${d.getDate()}`;
  return d.getFullYear() === today.getFullYear() ? base : `${base}/${String(d.getFullYear()).slice(2)}`;
}

const sideName = (side, firmName) => (side === 'firm' ? (firmName || 'Your firm') : side === 'pca' ? 'Prosperity' : 'Shared');

const CSS = `
.tdash{--bg:#E9E6E1;--card:#FFFFFF;--ink:#58585B;--head:#0A2947;--brand:#0069A6;--gold:#D09B2C;--orange:#F7931D;--line:#D4D0C9;--muted:#6B6C70;--soft:#F6F4F1;
  background:var(--bg);color:var(--ink);font:15px/1.5 "Helvetica Neue",Helvetica,Arial,sans-serif;}
.tdash *{box-sizing:border-box}
.tdash h1,.tdash h2,.tdash h3{margin:0;color:var(--head);font-family:"Avenir Black","Avenir","Montserrat",Arial,sans-serif}
.tdash .wrap{max-width:1080px;margin:0 auto;padding:0 20px 40px}
.tdash .hero{background:var(--head);color:#fff;padding:28px 0 26px}
.tdash .hero .wrap{padding-bottom:0}
.tdash .hero-top{display:flex;flex-wrap:wrap;gap:20px;justify-content:space-between;align-items:flex-end}
.tdash .brandline{color:var(--gold);font-weight:700;font-size:13px;letter-spacing:.04em;margin-bottom:6px}
.tdash .hero h1{color:#fff;font-size:clamp(26px,5vw,40px);line-height:1.08;text-transform:uppercase;font-weight:900}
.tdash .hero .sub{color:#E9E6E1;margin:8px 0 0;max-width:46ch}
.tdash .countdown{text-align:right}
.tdash .countdown .n{font:900 clamp(44px,8vw,72px)/1 "Avenir Black","Montserrat",Arial,sans-serif;color:var(--gold)}
.tdash .countdown .l{color:#E9E6E1;font-size:14px}
.tdash .status{display:flex;gap:14px;align-items:flex-start;background:var(--card);border-left:8px solid var(--gold);border-radius:4px;padding:16px 18px;margin-top:-18px;position:relative;box-shadow:0 1px 0 var(--line)}
.tdash .status .pill{flex:none;color:#0A2947;font-weight:700;font-size:13px;padding:4px 12px;border-radius:999px;white-space:nowrap}
.tdash .status p{margin:0}
.tdash .status strong{color:var(--head)}
.tdash section{margin-top:28px}
.tdash section>h2{font-size:15px;text-transform:uppercase;letter-spacing:.02em;margin-bottom:12px;padding-bottom:6px;border-bottom:3px solid var(--gold)}
.tdash .hint{color:var(--muted);font-size:13px;margin:-4px 0 12px}
.tdash .stages{display:grid;gap:4px}
.tdash .stage{padding:10px 8px;background:var(--soft);border:1px solid var(--line);font-size:13px;text-align:center;color:var(--muted);line-height:1.25}
.tdash .stage b{display:block;font-size:12px}
.tdash .stage.done{background:var(--head);color:#fff;border-color:var(--head)}
.tdash .stage.now{background:var(--brand);color:#fff;border-color:var(--brand);font-weight:700}
.tdash .tl{position:relative;height:150px;margin:26px 56px 0}
.tdash .tl .rail{position:absolute;left:0;right:0;top:70px;height:6px;background:var(--line);border-radius:3px}
.tdash .mk{position:absolute;top:62px;width:0}
.tdash .mk i{position:absolute;left:-7px;top:0;width:14px;height:14px;border-radius:50%;background:var(--card);border:3px solid var(--head)}
.tdash .mk.go i{background:var(--gold);width:20px;height:20px;left:-10px;top:-3px}
.tdash .mk.today i{background:var(--brand);border-color:var(--brand)}
.tdash .mk span{position:absolute;left:0;transform:translateX(-50%);width:104px;text-align:center;font-size:12px;line-height:1.25;color:var(--ink)}
.tdash .mk span b{display:block;color:var(--head);font-size:12px}
.tdash .mk.up span{bottom:26px}
.tdash .mk.dn span{top:28px}
.tdash .three{display:grid;grid-template-columns:repeat(3,1fr);gap:12px}
.tdash .thing{background:var(--card);border:1px solid var(--line);border-top:5px solid var(--brand);padding:14px 16px}
.tdash .when{display:inline-block;background:var(--soft);color:var(--head);font-weight:700;font-size:12px;padding:2px 8px;border-radius:3px;margin-bottom:8px}
.tdash .thing h3{font-size:15px;line-height:1.3;margin-bottom:6px;font-weight:800}
.tdash .thing p{margin:0;font-size:14px}
.tdash .later{margin-top:10px;font-size:14px;color:var(--muted)}
.tdash .ws{display:grid;grid-template-columns:repeat(3,1fr);gap:12px}
.tdash details.card{background:var(--card);border:1px solid var(--line);border-radius:4px}
.tdash details.card>summary{list-style:none;cursor:pointer;padding:14px 16px}
.tdash details.card>summary::-webkit-details-marker{display:none}
.tdash details.card[open]{grid-column:1/-1}
.tdash .card h3{font-size:15px;font-weight:800;line-height:1.25;margin-bottom:8px}
.tdash .row{display:flex;justify-content:space-between;align-items:center;gap:8px;margin-bottom:6px;font-size:13px}
.tdash .bar{height:8px;background:var(--line);border-radius:4px;overflow:hidden}
.tdash .bar i{display:block;height:100%;background:var(--brand)}
.tdash .chip{font-size:12px;font-weight:700;padding:2px 10px;border-radius:999px;color:#0A2947;white-space:nowrap}
.tdash .chip.g{background:#BFE0F2}.tdash .chip.a{background:var(--gold)}.tdash .chip.r{background:var(--orange)}.tdash .chip.n{background:var(--line)}
.tdash .next{font-size:13px;margin-top:8px}
.tdash .next b{color:var(--head)}
.tdash .card .items{border-top:1px solid var(--line);padding:6px 16px 12px}
.tdash .item{display:grid;grid-template-columns:1fr auto;gap:2px 12px;padding:8px 0;border-bottom:1px solid var(--line);font-size:14px}
.tdash .item:last-child{border-bottom:0}
.tdash .item .meta{grid-column:1/-1;color:var(--muted);font-size:12.5px}
.tdash .item.done .t{text-decoration:line-through;color:var(--muted)}
.tdash .hintopen{font-size:12px;color:var(--brand);margin-top:6px}
.tdash details[open] .hintopen{display:none}
.tdash .cols{display:grid;grid-template-columns:1.1fr .9fr;gap:12px}
.tdash .cols.even{grid-template-columns:1fr 1fr}
.tdash .panel{background:var(--card);border:1px solid var(--line);border-radius:4px;padding:16px}
.tdash .panel h3{font-size:14px;margin-bottom:10px}
.tdash .meter{margin-bottom:14px}
.tdash .meter .row b{color:var(--head)}
.tdash ul.plain{list-style:none;margin:0;padding:0}
.tdash ul.plain li{padding:8px 0;border-bottom:1px solid var(--line);font-size:14px}
.tdash ul.plain li:last-child{border-bottom:0}
.tdash ul.plain .d{display:inline-block;min-width:92px;padding-right:8px;font-weight:700;color:var(--head)}
.tdash ul.plain .s{display:block;color:var(--muted);font-size:12.5px;margin-left:92px}
.tdash .empty{color:var(--muted);font-size:14px}
.tdash .filters{display:flex;flex-wrap:wrap;gap:8px;margin-bottom:10px}
.tdash .filters button{font:inherit;font-size:13px;font-weight:700;color:var(--head);background:var(--card);border:2px solid var(--line);border-radius:999px;padding:5px 14px;cursor:pointer}
.tdash .filters button[aria-pressed="true"]{background:var(--head);color:#fff;border-color:var(--head)}
.tdash .filters button:focus-visible,.tdash details.card>summary:focus-visible,.tdash .res a:focus-visible{outline:3px solid var(--orange);outline-offset:2px}
.tdash .tablewrap{overflow-x:auto;background:var(--card);border:1px solid var(--line);border-radius:4px}
.tdash table{border-collapse:collapse;width:100%;min-width:640px;font-size:14px}
.tdash th{background:var(--head);color:#fff;text-align:left;font-size:12px;padding:8px 12px}
.tdash td{padding:8px 12px;border-bottom:1px solid var(--line);vertical-align:top}
.tdash td.due{white-space:nowrap}
.tdash td.due.late{color:#B4540A;font-weight:700}
.tdash .who{display:grid;grid-template-columns:repeat(3,1fr);gap:12px}
.tdash .who .panel b{color:var(--head);display:block}
.tdash .res{display:grid;grid-template-columns:repeat(2,1fr);gap:12px}
.tdash .res a{display:block;background:var(--card);border:1px solid var(--line);border-left:6px solid var(--gold);padding:14px 16px;color:inherit;text-decoration:none;border-radius:4px}
.tdash .res a:hover{border-left-color:var(--brand)}
.tdash .res b{display:block;color:var(--head);font-size:15px;margin-bottom:4px}
.tdash .res .when{margin:8px 0 0}
.tdash .res .go{display:block;margin-top:8px;font-size:13px;font-weight:700;color:var(--brand)}
.tdash .news{background:var(--card);border:1px solid var(--line);border-left:6px solid var(--brand);border-radius:4px;padding:14px 16px;margin-bottom:10px}
.tdash .news b{color:var(--head)}
.tdash footer{margin-top:32px;padding:16px 18px;background:var(--card);border-left:8px solid var(--brand);font-size:14px}
.tdash footer small{display:block;color:var(--muted);margin-top:6px}
@media (max-width:820px){
  .tdash .three,.tdash .ws,.tdash .who,.tdash .cols,.tdash .cols.even,.tdash .res{grid-template-columns:1fr}
  .tdash .stages{grid-template-columns:repeat(2,1fr)!important}
  .tdash .countdown{text-align:left}
  .tdash .tl{height:auto;margin:18px 0 0;padding-left:20px;border-left:6px solid var(--line)}
  .tdash .tl .rail{display:none}
  .tdash .mk{position:relative!important;left:auto!important;top:0;margin:0 0 14px;width:auto}
  .tdash .mk i{left:-30px;top:3px}
  .tdash .mk.go i{left:-33px;top:0}
  .tdash .mk span,.tdash .mk.up span,.tdash .mk.dn span{position:static;transform:none;width:auto;text-align:left}
}
`;

function Timeline({ milestones, goDate, today }) {
  // From today to Transition Day (or the last milestone when there's no date
  // yet). Milestones already behind us drop off - "Today" marks where we are.
  const ahead = milestones
    .map(m => ({ ...m, d: parseDay(m.entry_date) }))
    .filter(m => m.d && m.d >= today)
    .sort((a, b) => a.d - b.d);
  const end = goDate || (ahead.length ? ahead[ahead.length - 1].d : null);
  if (!end || end <= today) return null;
  const total = daysBetween(today, end);
  const marks = [
    { key: 'today', title: 'Today', sub: shortDate(today, today), d: today, cls: 'today' },
    ...ahead.filter(m => m.d <= end).map(m => ({
      key: m.id, title: m.title, d: m.d, cls: '',
      sub: `${shortDate(m.d, today)}${m.label ? ` ${m.label}` : ''}`,
    })),
    ...(goDate ? [{ key: 'go', title: 'Transition Day', sub: shortDate(goDate, today), d: goDate, cls: 'go' }] : []),
  ];
  return (
    <div className="tl" aria-label="Timeline of key dates">
      <div className="rail" />
      {marks.map((m, i) => (
        <div key={m.key} className={`mk ${m.cls} ${i % 2 === 0 ? 'up' : 'dn'}`}
          style={{ left: `${((daysBetween(today, m.d) / total) * 100).toFixed(2)}%` }}>
          <i /><span><b>{m.title}</b>{m.sub}</span>
        </div>
      ))}
    </div>
  );
}
Timeline.propTypes = { milestones: PropTypes.array.isRequired, goDate: PropTypes.instanceOf(Date), today: PropTypes.instanceOf(Date).isRequired };

// One area card: progress, health and what's next, opening to every item.
function AreaCard({ area, tasks, firmName, today }) {
  const done = tasks.filter(t => t.status === 'done').length;
  const open = tasks.filter(t => t.status !== 'done');
  const next = open.find(t => t.due_date) || open[0] || null;
  const overdue = open.some(t => { const d = parseDay(t.due_date); return d && d < today; });
  const soon = next && parseDay(next.due_date) && daysBetween(today, parseDay(next.due_date)) <= 7;
  const health = tasks.length === 0 ? { cls: 'n', label: 'No items yet' }
    : open.length === 0 ? { cls: 'g', label: 'Complete' }
    : overdue ? { cls: 'r', label: 'Overdue' }
    : soon ? { cls: 'a', label: 'Due soon' }
    : { cls: 'g', label: 'On track' };
  return (
    <details className="card">
      <summary>
        <h3>{area.name}</h3>
        <div className="row"><span>{done} of {tasks.length} done</span><span className={`chip ${health.cls}`}>{health.label}</span></div>
        <div className="bar"><i style={{ width: `${tasks.length ? (done / tasks.length) * 100 : 0}%` }} /></div>
        {next && (
          <div className="next">
            <b>Next:</b> {next.title}
            {next.due_date ? `, ${shortDate(parseDay(next.due_date), today)}` : ''}
            {next.owner_name ? ` (${next.owner_name})` : ''}
          </div>
        )}
        {tasks.length > 0 && (
          <div className="hintopen">{tasks.length === 1 ? 'Show the 1 item' : `Show all ${tasks.length} items`}</div>
        )}
      </summary>
      {tasks.length > 0 && (
        <div className="items">
          {tasks.map(t => {
            const st = TASK_STATUS[t.status] || TASK_STATUS.not_started;
            return (
              <div key={t.id} className={`item${t.status === 'done' ? ' done' : ''}`}>
                <span className="t">{t.title}</span>
                <span className={`chip ${st.cls}`}>{st.label}</span>
                <span className="meta">
                  {[t.owner_name, sideName(t.side, firmName), t.due_date ? `due ${shortDate(parseDay(t.due_date), today)}` : null]
                    .filter(Boolean).join(' · ')}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </details>
  );
}
AreaCard.propTypes = {
  area: PropTypes.object.isRequired, tasks: PropTypes.array.isRequired,
  firmName: PropTypes.string, today: PropTypes.instanceOf(Date).isRequired,
};

// A dated list ("Waiting on", "Decided recently", "Coming up"): the typed
// label wins over the date, so staff can write "Open" or "Tue 10/27".
function PlainList({ items, today, empty }) {
  if (!items.length) return <div className="empty">{empty}</div>;
  return (
    <ul className="plain">
      {items.map(e => (
        <li key={e.id}>
          <span className="d">{e.label || shortDate(parseDay(e.entry_date), today) || ' '}</span>
          {e.title}
          {e.detail && <span className="s">{e.detail}</span>}
        </li>
      ))}
    </ul>
  );
}
PlainList.propTypes = { items: PropTypes.array.isRequired, today: PropTypes.instanceOf(Date).isRequired, empty: PropTypes.string };

export default function TransitionDashboard({ data, announcements }) {
  const today = useMemo(startOfToday, []);
  const [filter, setFilter] = useState('all');
  const { plan, areas, tasks, entries, firm_name: firmName } = data;
  const e = entries || {};
  const goDate = parseDay(plan.transition_date);
  const daysToGo = goDate ? daysBetween(today, goDate) : null;
  const status = STATUS[plan.status] || STATUS.on_track;
  const stages = e.stage || [];

  const byDate = (a, b) => (parseDay(a.entry_date) || 0) - (parseDay(b.entry_date) || 0);
  const openTasks = tasks.filter(t => t.status !== 'done');
  const counts = { all: openTasks.length, firm: 0, pca: 0, joint: 0 };
  openTasks.forEach(t => { counts[t.side] = (counts[t.side] || 0) + 1; });
  const shown = openTasks.filter(t => filter === 'all' || t.side === filter);
  const areaName = Object.fromEntries(areas.map(a => [a.id, a.name]));

  const hasReadiness = [plan.accounts_entered, plan.checks_passed_pct, plan.open_exceptions].some(v => v !== null && v !== undefined);
  const enteredPct = plan.accounts_entered != null && plan.accounts_expected
    ? Math.min(100, (plan.accounts_entered / plan.accounts_expected) * 100) : null;
  const firstContact = (e.contact || [])[0];
  const updated = plan.updated ? new Date(plan.updated) : null;

  return (
    <div className="tdash">
      <style>{CSS}</style>
      <header className="hero">
        <div className="wrap">
          <div className="hero-top">
            <div>
              <div className="brandline">PROSPERITY CAPITAL ADVISORS</div>
              <h1>Your transition<br />{firmName}</h1>
              <p className="sub">Where you are, what we need from you, and what comes next.</p>
            </div>
            {goDate && (
              <div className="countdown">
                <div className="n">{Math.abs(daysToGo)}</div>
                <div className="l">
                  {daysToGo > 0 ? `day${daysToGo === 1 ? '' : 's'} to Transition Day`
                    : daysToGo === 0 ? 'Transition Day is today'
                    : `day${daysToGo === -1 ? '' : 's'} since Transition Day`}
                  <br />{WEEKDAY[goDate.getDay()]} {shortDate(goDate, new Date(0))}
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="wrap">
        <div className="status" role="status">
          <span className="pill" style={{ background: status.bg }}>{status.label}</span>
          <p>
            {plan.status_headline && <strong>{plan.status_headline}</strong>}
            {plan.status_headline && plan.status_detail ? ' ' : ''}
            {plan.status_detail}
            {!plan.status_headline && !plan.status_detail && 'Your Transition Manager will post an update here each week.'}
          </p>
        </div>

        {(announcements || []).length > 0 && (
          <section aria-labelledby="h-news">
            <h2 id="h-news">From Prosperity</h2>
            {announcements.map(a => (
              <div key={a.id} className="news">
                <b>{a.title}</b>
                {a.event_date && <span className="when" style={{ marginLeft: 8, marginBottom: 0 }}>{shortDate(parseDay(a.event_date), today)}</span>}
                {a.body && <AnnouncementBody style={{ fontSize: 14, marginTop: 4 }}>{a.body}</AnnouncementBody>}
              </div>
            ))}
          </section>
        )}

        {stages.length > 0 && (
          <section aria-labelledby="h-road">
            <h2 id="h-road">Where you are</h2>
            <div className="stages" style={{ gridTemplateColumns: `repeat(${stages.length},1fr)` }} aria-label="Transition stages">
              {stages.map((s, i) => {
                const n = i + 1;
                const state = n < plan.current_stage ? 'done' : n === plan.current_stage ? 'now' : '';
                return (
                  <div key={s.id} className={`stage ${state}`}>
                    <b>{n} {s.title}</b>
                    {state === 'now' ? 'You are here' : state === 'done' ? (s.label || 'Complete') : (s.label || '')}
                  </div>
                );
              })}
            </div>
            <Timeline milestones={e.milestone || []} goDate={goDate} today={today} />
          </section>
        )}

        {(e.week || []).length > 0 && (
          <section aria-labelledby="h-3">
            <h2 id="h-3">Your {e.week.length === 1 ? 'one thing' : `${e.week.length} things`} this week</h2>
            <div className="three">
              {e.week.map(w => (
                <article key={w.id} className="thing">
                  {w.label && <span className="when">{w.label}</span>}
                  <h3>{w.title}</h3>
                  {w.detail && <p>{w.detail}</p>}
                </article>
              ))}
            </div>
            {plan.later_note && <p className="later">{plan.later_note}</p>}
          </section>
        )}

        {areas.length > 0 && (
          <section aria-labelledby="h-ws">
            <h2 id="h-ws">Progress by area</h2>
            <p className="hint">Select an area to see every item, who owns it and when it is due.</p>
            <div className="ws">
              {areas.map(a => (
                <AreaCard key={a.id} area={a} tasks={tasks.filter(t => t.area_id === a.id)} firmName={firmName} today={today} />
              ))}
            </div>
          </section>
        )}

        <section aria-labelledby="h-data">
          <h2 id="h-data">Your data and what we&apos;re waiting on</h2>
          <div className={hasReadiness ? 'cols' : ''}>
            {hasReadiness && (
              <div className="panel">
                <h3>Spreadsheet readiness</h3>
                {plan.accounts_entered != null && (
                  <div className="meter">
                    <div className="row"><span>Accounts entered</span>
                      <b>{plan.accounts_entered}{plan.accounts_expected ? ` of about ${plan.accounts_expected}` : ''}</b></div>
                    {enteredPct !== null && <div className="bar"><i style={{ width: `${enteredPct}%` }} /></div>}
                  </div>
                )}
                {plan.checks_passed_pct != null && (
                  <div className="meter">
                    <div className="row"><span>Entries that passed custodian checks</span><b>{plan.checks_passed_pct}%</b></div>
                    <div className="bar"><i style={{ width: `${plan.checks_passed_pct}%` }} /></div>
                  </div>
                )}
                {plan.open_exceptions != null && (
                  <div className="meter">
                    <div className="row"><span>Open exceptions</span><b>{plan.open_exceptions}</b></div>
                  </div>
                )}
                {plan.readiness_note && <p className="hint" style={{ margin: '8px 0 0' }}>{plan.readiness_note}</p>}
              </div>
            )}
            <div className="panel">
              <h3>Waiting on</h3>
              <PlainList items={[...(e.waiting || [])].sort(byDate)} today={today} empty="Nothing outstanding right now." />
            </div>
          </div>
        </section>

        {((e.decision || []).length > 0 || (e.event || []).length > 0) && (
          <section>
            <div className="cols even">
              <div className="panel">
                <h3>Decided recently</h3>
                <PlainList items={[...(e.decision || [])].sort((a, b) => byDate(b, a))} today={today} empty="No decisions recorded yet." />
              </div>
              <div className="panel">
                <h3>Coming up</h3>
                <PlainList items={[...(e.event || [])].filter(x => { const d = parseDay(x.entry_date); return !d || d >= today; }).sort(byDate)}
                  today={today} empty="Nothing scheduled yet." />
              </div>
            </div>
          </section>
        )}

        {tasks.length > 0 && (
          <section aria-labelledby="h-all">
            <h2 id="h-all">Every open item</h2>
            <div className="filters" role="group" aria-label="Filter by owner">
              {[['all', 'All'], ['firm', firmName || 'Your firm'], ['pca', 'Prosperity'], ['joint', 'Shared']].map(([k, label]) => (
                <button key={k} type="button" aria-pressed={filter === k} onClick={() => setFilter(k)}>{label} {counts[k] || 0}</button>
              ))}
            </div>
            <div className="tablewrap">
              <table>
                <thead><tr><th>Item</th><th>Area</th><th>Owner</th><th>Due</th><th>Status</th></tr></thead>
                <tbody>
                  {shown.map(t => {
                    const d = parseDay(t.due_date);
                    const st = TASK_STATUS[t.status] || TASK_STATUS.not_started;
                    return (
                      <tr key={t.id}>
                        <td>{t.title}</td>
                        <td>{areaName[t.area_id]}</td>
                        <td>{t.owner_name || sideName(t.side, firmName)}</td>
                        <td className={`due${d && d < today ? ' late' : ''}`}>{d ? shortDate(d, today) : '-'}</td>
                        <td><span className={`chip ${st.cls}`}>{st.label}</span></td>
                      </tr>
                    );
                  })}
                  {shown.length === 0 && <tr><td colSpan={5} className="empty">Nothing open here.</td></tr>}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {(e.link || []).length > 0 && (
          <section aria-labelledby="h-learn">
            <h2 id="h-learn">Learn and reference</h2>
            <p className="hint">Always the current version. Open the link instead of saving a copy, because these documents change during the year.</p>
            <div className="res">
              {e.link.map(l => (
                <a key={l.id} href={l.url} target="_blank" rel="noopener noreferrer">
                  <b>{l.title}</b>
                  {l.detail}
                  {l.label && <span className="when">{l.label}</span>}
                  <span className="go">Open (opens in a new tab)</span>
                </a>
              ))}
            </div>
          </section>
        )}

        {(e.contact || []).length > 0 && (
          <section aria-labelledby="h-who">
            <h2 id="h-who">Who to call</h2>
            <div className="who">
              {e.contact.map(c => (
                <div key={c.id} className="panel"><b>{c.title}</b>{c.detail}</div>
              ))}
            </div>
          </section>
        )}

        <footer>
          {firstContact
            ? <>Something doesn&apos;t match what you heard? Tell {firstContact.title.split(' ')[0]}. We fix it within 48 hours and send you one answer.</>
            : <>Something doesn&apos;t match what you heard? Tell your Transition Manager.</>}
          <small>
            {updated ? `Last updated ${WEEKDAY[updated.getDay()]} ${shortDate(updated, new Date(0))}. ` : ''}
            For financial professional use only. Not for use with the general public.
          </small>
        </footer>
      </main>
    </div>
  );
}
TransitionDashboard.propTypes = {
  data: PropTypes.shape({
    firm_name: PropTypes.string,
    plan: PropTypes.object.isRequired,
    areas: PropTypes.array.isRequired,
    tasks: PropTypes.array.isRequired,
    entries: PropTypes.object,
  }).isRequired,
  announcements: PropTypes.array,
};
